import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import { RefreshCw, SlidersHorizontal } from "lucide-react";

import { ConfirmationDialog } from "@/components/confirmation-dialog";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DisabledReasonTooltip } from "@/components/ui/disabled-reason-tooltip";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RequiresCapability, useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";
import { fromDateInputValue, toDateInputValue } from "@/lib/date-input";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { formatRelativeTime } from "@/lib/utils";

type TemplateColumn =
  | { key: string; header: string; type: "title" }
  | { key: string; header: string; type: "normal"; defaultValue?: string }
  | { key: string; header: string; type: "generated"; description: string }
  | { key: string; header: string; type: "description" }
  | { key: string; header: string; type: "source_column"; sourceHeader: string; fallbackValue?: string }
  | { key: string; header: string; type: "csi"; csiField: string }
  | { key: string; header: string; type: "team_member"; defaultValue?: string }
  | { key: string; header: string; type: "project_item"; defaultValue?: string }
  | { key: string; header: string; type: "duration"; defaultValue?: string }
  | { key: string; header: string; type: "date"; defaultValue?: string };

const CONFIGURABLE_TEMPLATE_COLUMN_TYPES = new Set<TemplateColumn["type"]>([
  "normal",
  "team_member",
  "project_item",
  "duration",
  "date",
]);

function isSourceTemplateColumn(
  column: TemplateColumn
): column is Extract<TemplateColumn, { type: "source_column" }> {
  return column.type === "source_column";
}

function isConfigurableTemplateColumn(
  column: TemplateColumn
): column is Extract<TemplateColumn, { defaultValue?: string }> {
  return CONFIGURABLE_TEMPLATE_COLUMN_TYPES.has(column.type);
}

function overrideKey(column: TemplateColumn) {
  return isSourceTemplateColumn(column) ? `source_header:${column.key}` : column.key;
}

/** A custom template's own defaults: manual column values and schedule column names. */
function defaultOverrides(columns: TemplateColumn[]) {
  const defaults: Record<string, string> = {};
  for (const column of columns) {
    if (isConfigurableTemplateColumn(column)) defaults[column.key] = column.defaultValue ?? "";
    if (isSourceTemplateColumn(column)) defaults[overrideKey(column)] = column.sourceHeader ?? "";
  }
  return defaults;
}

type TemplateOption = {
  id: string;
  source: "builtin" | "custom";
  name: string;
  description: string;
  version: number;
  columns: TemplateColumn[];
  archived?: boolean;
};

export type GenerateOptions = {
  templateId?: string;
  templateDefaultOverrides?: Record<string, string>;
};

interface SubmittalBarProps {
  mode: "generate" | "regenerate" | "retry";
  selectedTableCount: number;
  totalTableCount: number;
  activeMergeCount: number;
  isGenerating: boolean;
  onGenerate: (options: GenerateOptions) => Promise<void>;
  /** The current generated submittal, for completed jobs. */
  generated?: {
    generatedAt: number;
    hasEditedData: boolean;
    templateId?: string;
    hasUngeneratedChanges: boolean;
  };
}

// Regenerate stays secondary to View Submittal; "Changes Not Generated" flags when it's needed.
const GENERATE_VARIANTS: Record<SubmittalBarProps["mode"], "default" | "outline"> = {
  generate: "default",
  regenerate: "outline",
  retry: "default",
};

const GENERATE_LABELS: Record<SubmittalBarProps["mode"], string> = {
  generate: "Generate Submittal",
  regenerate: "Regenerate",
  retry: "Retry Generation",
};

/**
 * Docked bar at the bottom of the job page: what will go in the submittal, the template, and
 * the Generate / Regenerate action.
 */
export function SubmittalBar({
  mode,
  selectedTableCount,
  totalTableCount,
  activeMergeCount,
  isGenerating,
  onGenerate,
  generated,
}: SubmittalBarProps) {
  const canEdit = useCanEdit();
  const appNavigate = useAppNavigate();
  const { data: templateOptions } = useQuery(
    convexQuery(api.templates.list, { includeArchived: false })
  );
  const templates = (templateOptions ?? []) as TemplateOption[];
  const [chosenTemplateId, setChosenTemplateId] = useState<string>();
  // Overrides are tagged with their template so switching templates starts from its defaults.
  const [editedOverrides, setEditedOverrides] = useState<{
    templateKey: string;
    values: Record<string, string>;
  }>();
  const [overridesOpen, setOverridesOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  useOnEditAccessLost(() => {
    setOverridesOpen(false);
    setConfirmOpen(false);
  });

  // Until one is chosen: the template the current submittal used, else the first one.
  const selectedTemplate =
    templates.find((template) => template.id === chosenTemplateId) ??
    templates.find((template) => template.id === generated?.templateId) ??
    templates[0];
  const overrideColumns =
    selectedTemplate?.source === "custom"
      ? [
          ...selectedTemplate.columns.filter(isConfigurableTemplateColumn),
          ...selectedTemplate.columns.filter(isSourceTemplateColumn),
        ]
      : [];
  const templateKey = selectedTemplate ? `${selectedTemplate.id}:${selectedTemplate.version}` : "";
  const overrides =
    editedOverrides?.templateKey === templateKey
      ? editedOverrides.values
      : defaultOverrides(overrideColumns);

  function setOverride(key: string, value: string) {
    setEditedOverrides({ templateKey, values: { ...overrides, [key]: value } });
  }

  async function generate() {
    await onGenerate({
      templateId: selectedTemplate?.id,
      templateDefaultOverrides: overrides,
    });
  }

  function handleGenerateClick() {
    // Regenerating replaces the submittal, so confirm only when that would discard manual edits.
    if (mode === "regenerate" && generated?.hasEditedData) {
      setConfirmOpen(true);
      return;
    }
    void generate();
  }

  const hasChanges = generated?.hasUngeneratedChanges ?? false;
  const noTables = selectedTableCount === 0;

  return (
    <div
      data-slot="submittal-bar"
      className="sticky bottom-0 z-20 -mx-6 -mb-6 mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 border-t-2 border-border bg-card px-6 py-3 lg:-mx-8 lg:-mb-8 lg:px-8"
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
        {generated ? (
          <>
            <span>Generated {formatRelativeTime(generated.generatedAt)}</span>
            {generated.hasEditedData ? (
              <StatusBadge status="review" label="Edited Version" showSpinner={false} />
            ) : null}
            {hasChanges ? (
              <StatusBadge status="pending" label="Changes Not Generated" showSpinner={false} />
            ) : null}
            <span aria-hidden="true" className="mx-1 h-4 w-px bg-border" />
          </>
        ) : null}
        <span>
          <span className="font-semibold text-foreground">{selectedTableCount}</span> of{" "}
          {totalTableCount} tables
        </span>
        <span aria-hidden="true">·</span>
        <span>
          <span className="font-semibold text-foreground">{activeMergeCount}</span>{" "}
          {activeMergeCount === 1 ? "merge" : "merges"}
        </span>
      </div>

      <div className="ms-auto flex flex-wrap items-center gap-2">
        {canEdit ? (
          <>
            <Label htmlFor="submittal-template">Template</Label>
            <Select value={selectedTemplate?.id ?? ""} onValueChange={setChosenTemplateId}>
              <SelectTrigger id="submittal-template" size="sm" className="w-56">
                <SelectValue placeholder="Choose a template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                    {template.source === "custom" ? ` v${template.version}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {overrideColumns.length > 0 ? (
              <IconButton
                label="Template Overrides"
                icon={SlidersHorizontal}
                variant="outline"
                onClick={() => setOverridesOpen(true)}
              />
            ) : null}
          </>
        ) : null}
        <DisabledReasonTooltip
          reason={canEdit && noTables ? "Include at least one table to generate" : undefined}
        >
          <RequiresCapability mode="disable">
            <Button
              size="sm"
              variant={GENERATE_VARIANTS[mode]}
              disabled={isGenerating || noTables}
              onClick={handleGenerateClick}
            >
              {mode === "generate" ? null : <RefreshCw />}
              {isGenerating ? "Generating..." : GENERATE_LABELS[mode]}
            </Button>
          </RequiresCapability>
        </DisabledReasonTooltip>
      </div>

      <Dialog open={overridesOpen} onOpenChange={setOverridesOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Template Overrides</DialogTitle>
            <DialogDescription>
              Override manual defaults or schedule column names for this generation only.
            </DialogDescription>
          </DialogHeader>
          <div className="grid max-h-[55dvh] gap-3 overflow-y-auto py-1 sm:grid-cols-2">
            {overrideColumns.map((column) => {
              const key = overrideKey(column);
              return (
                <div key={key} className="space-y-1">
                  <Label htmlFor={`template-override-${key}`} className="text-xs">
                    {column.header}
                    {isSourceTemplateColumn(column) ? " column name" : ""}
                  </Label>
                  <Input
                    id={`template-override-${key}`}
                    type={column.type === "date" ? "date" : column.type === "duration" ? "number" : "text"}
                    value={
                      column.type === "date" ? toDateInputValue(overrides[key]) : overrides[key] ?? ""
                    }
                    onChange={(event) =>
                      setOverride(
                        key,
                        column.type === "date"
                          ? fromDateInputValue(event.target.value)
                          : event.target.value
                      )
                    }
                    placeholder={
                      isSourceTemplateColumn(column) ? "e.g. Comment, Comments, Remarks" : "Default value"
                    }
                  />
                </div>
              );
            })}
          </div>
          <DialogFooter className="sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={() => appNavigate({ to: "/specPilot/templates", title: "Manage Templates" })}
            >
              Manage Templates
            </Button>
            <Button type="button" onClick={() => setOverridesOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Regenerate submittal?"
        description="This replaces the current submittal and discards the edits made in the submittal viewer."
        confirmLabel="Regenerate"
        onConfirm={generate}
      />
    </div>
  );
}
